import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import {
  COMPETITION_TYPE_LABEL as TYPE_LABEL, COMPETITION_TYPE_ORDER as TYPE_ORDER, NON_LEAGUE_TYPES,
} from '../lib/competitionType'
import { fmtDate } from '../lib/format'
import type { MatchListItem } from '../lib/types'

const seasonKeyOf = (m: MatchListItem) => m.season_label ?? m.season_name ?? '—'
const typeKeyOf = (m: MatchListItem) => m.competition_type ?? 'inne'

interface SeasonOpt { key: string; label: string; count: number; last: string }

// Ta sama lista dla pierwszego zespołu (`/raporty`) i Legii II (`/raporty/legia-ii`,
// pages/ReservesReport.tsx) — różni się drużyną, adresem raportu i podpisem.
interface Props {
  teamId?: number
  base?: string
  teamLabel?: string
  title?: string
}

export function MatchList({ teamId, base = '/raporty', teamLabel = 'Legia', title = 'Raporty Meczowe' }: Props = {}) {
  const [matches, setMatches] = useState<MatchListItem[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // null = brak jawnego wyboru → używamy najnowszego sezonu (patrz effectiveSeason).
  const [season, setSeason] = useState<string | null>(null)
  // Typy odznaczone (ukryte). Domyślnie WSZYSTKO POZA LIGĄ — sztab wchodzi tu po
  // raport ligowy, a puchar/sparing/europa doklejają się pigułkami.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(() => new Set(NON_LEAGUE_TYPES))

  useEffect(() => {
    api.matches(undefined, teamId)
      .then(setMatches)
      .catch((e) => setErr(String(e)))
  }, [teamId])

  // Sezony sportowe z danych — najnowszy pierwszy (po ostatnim meczu sezonu).
  const seasons = useMemo<SeasonOpt[]>(() => {
    if (!matches) return []
    const byKey = new Map<string, SeasonOpt>()
    for (const m of matches) {
      const key = seasonKeyOf(m)
      const cur = byKey.get(key)
      if (cur) {
        cur.count += 1
        if (m.match_date > cur.last) cur.last = m.match_date
      } else {
        byKey.set(key, { key, label: key, count: 1, last: m.match_date })
      }
    }
    return [...byKey.values()].sort((a, b) => b.last.localeCompare(a.last))
  }, [matches])

  // Efektywny sezon = jawny wybór, inaczej najnowszy (dziś = 2026/27).
  const effectiveSeason = season ?? seasons[0]?.key ?? null

  const inSeason = useMemo(
    () => (matches && effectiveSeason != null ? matches.filter((m) => seasonKeyOf(m) === effectiveSeason) : []),
    [matches, effectiveSeason],
  )

  // Typy rozgrywek obecne w wybranym sezonie (do checkboxów).
  const typesInSeason = useMemo(() => {
    const present = new Set(inSeason.map(typeKeyOf))
    return TYPE_ORDER.filter((t) => present.has(t))
  }, [inSeason])

  const filtered = useMemo(
    () => inSeason.filter((m) => !hiddenTypes.has(typeKeyOf(m))),
    [inSeason, hiddenTypes],
  )

  const toggleType = (t: string) =>
    setHiddenTypes((prev) => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold leading-tight text-ink-900">{title}</h1>
        <p className="mt-1 text-sm text-ink-500">
          Wybierz mecz — Warstwa I: KPI wyniku. Domyślnie tylko liga; puchar, sparingi i europę
          dokładasz pigułkami „Rozgrywki".
        </p>
      </div>

      {/* Pasek filtrów — sezon + rodzaj rozgrywek */}
      {matches && matches.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-400">Sezon</label>
            <select
              value={effectiveSeason ?? ''}
              onChange={(e) => setSeason(e.target.value)}
              className="min-w-[10rem] rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-forest-500 focus:outline-none focus:ring-2 focus:ring-forest-200"
            >
              {seasons.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label} ({s.count})
                </option>
              ))}
            </select>
          </div>

          {/* Pasek pokazujemy też przy jednym typie, jeśli cokolwiek jest UKRYTE —
              inaczej przedsezonie (same sparingi) wyszłoby puste bez sposobu, by
              filtr włączyć z powrotem. */}
          {(typesInSeason.length > 1 || typesInSeason.some((t) => hiddenTypes.has(t))) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">Rozgrywki</span>
              {typesInSeason.map((t) => {
                const on = !hiddenTypes.has(t)
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    aria-pressed={on}
                    className={
                      'rounded-full border px-3 py-1 text-xs font-semibold transition ' +
                      (on
                        ? 'border-forest-300 bg-forest-50 text-forest-800'
                        : 'border-ink-200 bg-white text-ink-400 line-through')
                    }
                  >
                    {TYPE_LABEL[t] ?? t}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="rounded-xl border border-bad-200 bg-bad-50 px-4 py-3 text-sm text-bad-700">
          Nie udało się pobrać meczów: {err}
        </div>
      )}
      {!err && matches === null && (
        <div className="rounded-xl border border-ink-200 bg-white px-4 py-8 text-center text-ink-400">
          Ładowanie…
        </div>
      )}
      {matches && matches.length === 0 && (
        <div className="rounded-xl border border-ink-200 bg-white px-4 py-8 text-center text-ink-400">
          Brak meczów do wyświetlenia.
        </div>
      )}
      {matches && matches.length > 0 && filtered.length === 0 && (
        <div className="rounded-xl border border-ink-200 bg-white px-4 py-8 text-center text-ink-400">
          Brak meczów ligowych w tym sezonie — włącz pozostałe rozgrywki pigułkami wyżej.
        </div>
      )}

      {filtered.length > 0 && (
        <ul className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-card">
          {filtered.map((m) => {
            const tone =
              m.result === 'W' ? 'bg-good-100 text-good-700' :
              m.result === 'L' ? 'bg-bad-100 text-bad-700' :
              'bg-ink-100 text-ink-500'
            const compLabel = m.competition_name
              ?? (m.competition_type ? TYPE_LABEL[m.competition_type] ?? m.competition_type : null)
            return (
              <li key={m.match_id}>
                <Link
                  to={`${base}/${m.match_id}`}
                  className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-ink-50"
                >
                  <span className={`legia-chip ${tone}`}>{m.result}</span>
                  <div className="min-w-[88px] text-sm font-medium text-ink-500">
                    {fmtDate(m.match_date)}
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-ink-900">
                      {teamLabel} <span className="text-ink-400">vs</span> {m.opposition_name}
                    </div>
                    <div className="text-xs text-ink-400">
                      {m.venue === 'home' ? 'u siebie' : 'na wyjeździe'}
                      {compLabel ? ` · ${compLabel}` : ''}
                    </div>
                  </div>
                  <div className="kpi-num text-2xl font-bold tabular-nums text-ink-900">
                    {m.score}
                  </div>
                  <span className="text-ink-300">→</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
